import { useEffect, useMemo, useState } from "react";

import {
  observationPayloadSchemas,
  type TimingPrecision,
} from "../../../../shared/src/index.js";
import { ApiError, type ApiClient } from "../../api/client.js";
import type {
  CorrectedObservation,
  ObservationCorrectionContext,
} from "../../api/types.js";
import { createClientRequestId, formatDate } from "../../app/presentation.js";

interface ObservationCorrectionFormProps {
  api: ApiClient;
  observationId: string;
  onClose: () => void;
  onSaved: (result: CorrectedObservation) => Promise<void> | void;
  onStale: () => Promise<void> | void;
}

type Payload = Record<string, unknown>;

export function ObservationCorrectionForm({
  api,
  observationId,
  onClose,
  onSaved,
  onStale,
}: ObservationCorrectionFormProps) {
  const clientRequestId = useMemo(createClientRequestId, []);
  const [context, setContext] = useState<ObservationCorrectionContext | null>(
    null,
  );
  const [payload, setPayload] = useState<Payload>({});
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void api
      .getObservationCorrectionContext(observationId)
      .then((value) => {
        if (!active) return;
        setContext(value);
        setPayload({ ...value.target.payload });
      })
      .catch((caught: unknown) => {
        if (active) {
          setError(
            caught instanceof Error
              ? caught.message
              : "The correction context is unavailable.",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api, observationId]);

  function setValue(key: string, value: unknown): void {
    setPayload((current) => {
      const next = { ...current };
      if (value === "" || value === undefined || value === null) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }

  function setNumberValue(key: string, value: string): void {
    setValue(key, value === "" ? undefined : Number(value));
  }

  function changeTimingPrecision(precision: TimingPrecision): void {
    const cycleMonths = payload.cycleMonths;
    const next: Payload = { precision };
    if (typeof cycleMonths === "number") next.cycleMonths = cycleMonths;
    switch (precision) {
      case "exact_date":
        next.exactDate = "";
        break;
      case "month":
        next.month = 1;
        break;
      case "date_range":
        next.rangeStart = "";
        next.rangeEnd = "";
        break;
      case "season":
        next.season = "spring";
        break;
      case "relative":
        next.relativeState = "due_soon";
        break;
      case "unknown":
        break;
    }
    setPayload(next);
  }

  async function submit(): Promise<void> {
    if (context === null) return;
    setSaving(true);
    setError(null);
    const parsed =
      observationPayloadSchemas[context.target.observationType].safeParse(
        payload,
      );
    if (!parsed.success) {
      setError(
        parsed.error.issues[0]?.message ?? "Check the correction fields.",
      );
      setSaving(false);
      return;
    }
    try {
      const result = await api.correctObservation(
        observationId,
        clientRequestId,
        parsed.data,
        note.trim() || undefined,
      );
      await onSaved(result);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "already_corrected") {
        setError(
          "This fact was already corrected elsewhere. Refreshing the current Lab understanding.",
        );
        await onStale();
      } else {
        setError(
          caught instanceof Error
            ? caught.message
            : "The correction could not be saved.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        className="capture-sheet correction-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="correction-title"
      >
        <div className="sheet-heading">
          <div>
            <p className="eyebrow">Correct recorded information</p>
            <h2 id="correction-title">
              {context === null
                ? "Correction"
                : `Correct ${correctionLabel(context.target.observationType)}`}
            </h2>
            {context && (
              <p className="context-line">
                Originally recorded {formatDate(context.target.observedAt)}
              </p>
            )}
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close correction form"
            disabled={saving}
          >
            ×
          </button>
        </div>

        {loading && <p className="muted">Loading recorded value…</p>}
        {!loading && context && (
          <>
            <div className="correction-original">
              <span className="label">Currently recorded</span>
              <b>{formatCorrectionValue(context.target.payload)}</b>
            </div>
            {context.target.observationType === "quantity" && (
              <QuantityCorrection
                payload={payload}
                setValue={setValue}
                setNumberValue={setNumberValue}
              />
            )}
            {context.target.observationType === "timing" && (
              <TimingCorrection
                payload={payload}
                setValue={setValue}
                setNumberValue={setNumberValue}
                changePrecision={changeTimingPrecision}
              />
            )}
            {context.target.observationType === "provider" && (
              <label>
                Provider
                <input
                  value={stringValue(payload.name)}
                  onChange={(event) => setValue("name", event.target.value)}
                />
              </label>
            )}
            {context.target.observationType === "accreditation_requirement" && (
              <label>
                Accreditation requirement
                <select
                  value={stringValue(payload.requirement)}
                  onChange={(event) =>
                    setValue("requirement", event.target.value)
                  }
                >
                  <option value="no">No</option>
                  <option value="unknown">Unknown</option>
                  <option value="yes">Yes</option>
                </select>
              </label>
            )}
            <label>
              Why? <span className="muted">optional</span>
              <textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={3}
                placeholder="Typo during field entry"
              />
            </label>
          </>
        )}
        {error && (
          <p className="error-message" role="alert">
            {error}
          </p>
        )}
        <div className="sheet-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => void submit()}
            disabled={loading || saving || context === null}
          >
            {saving ? "Saving…" : "Save correction"}
          </button>
        </div>
      </section>
    </div>
  );
}

function QuantityCorrection({
  payload,
  setValue,
  setNumberValue,
}: {
  payload: Payload;
  setValue: (key: string, value: unknown) => void;
  setNumberValue: (key: string, value: string) => void;
}) {
  const historical = payload.scope === "historical_service";
  return (
    <div className="form-grid compact-grid">
      <label>
        Quantity
        <input
          type="number"
          min="0"
          inputMode="numeric"
          value={numberValue(payload.value)}
          onChange={(event) => setNumberValue("value", event.target.value)}
        />
      </label>
      <label>
        Precision
        <select
          value={stringValue(payload.qualifier)}
          onChange={(event) => setValue("qualifier", event.target.value)}
        >
          <option value="approximate">Approximate</option>
          <option value="exact">Exact</option>
        </select>
      </label>
      <label>
        Quantity meaning
        <select
          value={stringValue(payload.scope)}
          onChange={(event) => setValue("scope", event.target.value)}
        >
          <option value="current_installed_base">Current equipment</option>
          <option value="historical_service">Historical service</option>
        </select>
      </label>
      <label>
        {historical ? "Service date" : "As-of date"}{" "}
        <span className="muted">optional</span>
        <input
          type="date"
          value={stringValue(payload.effectiveDate)}
          onChange={(event) => setValue("effectiveDate", event.target.value)}
          required={historical}
        />
      </label>
    </div>
  );
}

function TimingCorrection({
  payload,
  setValue,
  setNumberValue,
  changePrecision,
}: {
  payload: Payload;
  setValue: (key: string, value: unknown) => void;
  setNumberValue: (key: string, value: string) => void;
  changePrecision: (precision: TimingPrecision) => void;
}) {
  const precision = payload.precision as TimingPrecision;
  return (
    <div className="form-grid compact-grid">
      <label>
        Timing precision
        <select
          value={precision}
          onChange={(event) =>
            changePrecision(event.target.value as TimingPrecision)
          }
        >
          <option value="exact_date">Exact date</option>
          <option value="month">Month</option>
          <option value="date_range">Date range</option>
          <option value="season">Season</option>
          <option value="relative">Relative timing</option>
          <option value="unknown">Unknown</option>
        </select>
      </label>
      {precision === "exact_date" && (
        <label>
          Exact date
          <input
            type="date"
            value={stringValue(payload.exactDate)}
            onChange={(event) => setValue("exactDate", event.target.value)}
          />
        </label>
      )}
      {precision === "month" && (
        <>
          <label>
            Year <span className="muted">optional</span>
            <input
              type="number"
              min="2000"
              max="2200"
              value={numberValue(payload.year)}
              onChange={(event) => setNumberValue("year", event.target.value)}
            />
          </label>
          <label>
            Month
            <input
              type="number"
              min="1"
              max="12"
              value={numberValue(payload.month)}
              onChange={(event) => setNumberValue("month", event.target.value)}
            />
          </label>
        </>
      )}
      {precision === "date_range" && (
        <>
          <label>
            Range start
            <input
              type="date"
              value={stringValue(payload.rangeStart)}
              onChange={(event) => setValue("rangeStart", event.target.value)}
            />
          </label>
          <label>
            Range end
            <input
              type="date"
              value={stringValue(payload.rangeEnd)}
              onChange={(event) => setValue("rangeEnd", event.target.value)}
            />
          </label>
        </>
      )}
      {precision === "season" && (
        <>
          <label>
            Year <span className="muted">optional</span>
            <input
              type="number"
              min="2000"
              max="2200"
              value={numberValue(payload.year)}
              onChange={(event) => setNumberValue("year", event.target.value)}
            />
          </label>
          <label>
            Season
            <select
              value={stringValue(payload.season)}
              onChange={(event) => setValue("season", event.target.value)}
            >
              <option value="spring">Spring</option>
              <option value="summer">Summer</option>
              <option value="fall">Fall</option>
              <option value="winter">Winter</option>
            </select>
          </label>
        </>
      )}
      {precision === "relative" && (
        <>
          <label>
            Relative state
            <select
              value={stringValue(payload.relativeState)}
              onChange={(event) =>
                setValue("relativeState", event.target.value)
              }
            >
              <option value="due_soon">Due soon</option>
              <option value="overdue">Overdue</option>
              <option value="just_serviced">Just serviced</option>
            </select>
          </label>
          <label>
            Reference date <span className="muted">optional</span>
            <input
              type="date"
              value={stringValue(payload.referenceDate)}
              onChange={(event) =>
                setValue("referenceDate", event.target.value)
              }
            />
          </label>
        </>
      )}
      <label>
        Cycle months <span className="muted">optional</span>
        <input
          type="number"
          min="1"
          max="120"
          value={numberValue(payload.cycleMonths)}
          onChange={(event) =>
            setNumberValue("cycleMonths", event.target.value)
          }
        />
      </label>
    </div>
  );
}

function correctionLabel(
  type: ObservationCorrectionContext["target"]["observationType"],
): string {
  switch (type) {
    case "quantity":
      return "quantity";
    case "timing":
      return "timing";
    case "provider":
      return "provider";
    case "accreditation_requirement":
      return "accreditation requirement";
  }
}

function formatCorrectionValue(payload: Payload): string {
  if ("value" in payload) {
    return `${payload.qualifier === "approximate" ? "~" : ""}${String(payload.value)} ${payload.scope === "historical_service" ? "historically serviced" : "current instruments"}`;
  }
  if ("name" in payload) return String(payload.name);
  if ("requirement" in payload) return String(payload.requirement);
  if (payload.precision === "month") {
    return `${monthName(payload.month)}${payload.year ? ` ${payload.year}` : ""}`;
  }
  if (payload.precision === "date_range") {
    return `${String(payload.rangeStart)} – ${String(payload.rangeEnd)}`;
  }
  if (payload.precision === "season") {
    return `${String(payload.season)}${payload.year ? ` ${payload.year}` : ""}`;
  }
  if (payload.precision === "exact_date") return String(payload.exactDate);
  if (payload.precision === "relative") return String(payload.relativeState);
  return "Unknown";
}

function monthName(value: unknown): string {
  const month = Number(value);
  return new Intl.DateTimeFormat("en", { month: "long" }).format(
    new Date(Date.UTC(2020, month - 1, 1)),
  );
}

function stringValue(value: unknown): string {
  return typeof value === "string"
    ? value
    : value === undefined
      ? ""
      : String(value);
}

function numberValue(value: unknown): string {
  return typeof value === "number" ? String(value) : "";
}
